import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, Link2, Check, Copy, RefreshCw, EyeOff, Plus, Trash2,
  Star, Save, Film, Ruler, Mail, FileText, AlertTriangle, ExternalLink, Upload,
  Quote, ThumbsUp, ThumbsDown, Clock, Image as ImageIcon, Check as CheckIcon,
} from 'lucide-react';
import { authFetch, API } from '../../api';

const PUBLIC_BASE = window.location.origin;

const MEASURABLE_SOURCES = [
  { value: 'self_reported', label: 'Self-reported / family measured' },
  { value: 'school_staff',  label: 'Measured by school staff' },
  { value: 'camp',          label: 'Camp verified' },
  { value: 'combine',       label: 'Combine verified' },
];

const PROVIDERS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'hudl',    label: 'Hudl' },
  { value: 'vimeo',   label: 'Vimeo' },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[#B0BEC5] text-sm mb-1.5">{label}</label>
    {children}
    {hint && <div className="text-[#546E7A] text-xs mt-1">{hint}</div>}
  </div>
);

const input =
  'w-full bg-[#243B55] text-white px-3 py-2.5 rounded-lg border border-[#546E7A]/30 ' +
  'focus:border-[#00D4FF] focus:outline-none transition-colors text-sm';

const Card = ({ icon: Icon, title, subtitle, children }) => (
  <div className="panel-card mb-4">
    <div className="flex items-start gap-2.5 mb-4">
      <Icon size={18} className="text-[#00D4FF] flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <h3 className="text-white font-bold">{title}</h3>
        {subtitle && <p className="text-[#546E7A] text-xs mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const RecruitingTab = ({ player, onPlayerUpdate }) => {
  const [share, setShare] = useState(null);
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '', url: '', provider: 'youtube', description: '', is_featured: false,
  });
  const [addMode, setAddMode] = useState('upload');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [replacingId, setReplacingId] = useState(null);
  const [replacePct, setReplacePct] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  // What the server last gave us, so saves can send only what changed.
  const [baseline, setBaseline] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [measurables, setMeasurables] = useState([]);

  const pid = player?.id;

  const flash = (msg) => { setStatus(msg); setTimeout(() => setStatus(''), 2500); };

  const loadShare = useCallback(async () => {
    const r = await authFetch(`${API}/api/share/${pid}`);
    if (r.ok) setShare(await r.json());
  }, [pid]);

  const loadVideos = useCallback(async () => {
    const r = await authFetch(`${API}/api/share/${pid}/videos`);
    if (r.ok) setVideos(await r.json());
  }, [pid]);

  const loadTestimonials = useCallback(async () => {
    const r = await authFetch(`${API}/api/share/${pid}/testimonials`);
    if (r.ok) setTestimonials(await r.json());
  }, [pid]);

  const loadProfile = useCallback(async () => {
    // Deliberately NOT from the `player` prop: /api/players predates these
    // columns and returns none of them, so the form would load blank and a
    // save would erase whatever was already there.
    const r = await authFetch(`${API}/api/share/${pid}/profile`);
    if (!r.ok) return;
    const data = await r.json();
    const asForm = {};
    Object.entries(data).forEach(([k, v]) => { asForm[k] = v ?? ''; });
    setForm(asForm);
    setBaseline(asForm);
  }, [pid]);

  // Clear immediately when the player changes. Without this the previous
  // player's values stay on screen until the fetch returns -- and a save in
  // that window would write one athlete's details onto another.
  useEffect(() => {
    setForm({});
    setBaseline(null);
    setVideos([]);
    setTestimonials([]);
    setShare(null);
    setMeasurables([]);
  }, [pid]);

  useEffect(() => {
    if (!pid) return;
    loadShare();
    loadVideos();
    loadTestimonials();
    loadProfile();
  }, [pid, loadShare, loadVideos, loadTestimonials, loadProfile]);

  // Which measurables to ask for follows the position, from the same
  // definitions the athlete portal and the Player Profile use.
  useEffect(() => {
    const pos = player?.position;
    if (!pos) return;
    let cancelled = false;
    fetch(`${API}/api/athlete/measurables/${encodeURIComponent(pos)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setMeasurables(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [player?.position]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Share the readable slug; the random token stays valid as a fallback.
  const publicUrl = share?.slug
    ? `${PUBLIC_BASE}/p/${share.slug}`
    : share?.token ? `${PUBLIC_BASE}/p/${share.token}` : null;

  /* ── actions ─────────────────────────────────────────────── */

  const handleSave = async () => {
    if (!baseline) return;
    setSaving(true);
    try {
      // Only send fields the user actually touched. Sending the whole form
      // would null out anything this screen failed to load.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (String(v ?? '') === String(baseline[k] ?? '')) return;
        if (v === '' || v === null) { payload[k] = null; return; }
        if (['wingspan', 'hand_size', 'arm_length'].includes(k)) {
          const n = parseFloat(v);
          payload[k] = Number.isFinite(n) ? n : null;
        } else if (k === 'jersey_number') {
          const n = parseInt(v, 10);
          payload[k] = Number.isFinite(n) ? n : null;
        } else {
          payload[k] = v;
        }
      });

      if (Object.keys(payload).length === 0) {
        flash('Nothing changed');
        return;
      }

      const r = await authFetch(`${API}/api/share/${pid}/profile`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      await loadProfile();
      flash(`Saved ${Object.keys(payload).length} field${Object.keys(payload).length !== 1 ? 's' : ''} ✓`);
      if (onPlayerUpdate) onPlayerUpdate();
    } catch (e) {
      flash('Save failed');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const r = await authFetch(`${API}/api/players/${pid}/photo`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await r.text());
      flash('Photo updated ✓');
      if (onPlayerUpdate) onPlayerUpdate();
    } catch (e) {
      flash('Photo upload failed');
      console.error(e);
    } finally {
      setPhotoBusy(false);
    }
  };

  const doAction = async (path, okMsg) => {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/api/share/${pid}/${path}`, { method: 'POST' });
      if (!r.ok) throw new Error(await r.text());
      await loadShare();
      flash(okMsg);
    } catch (e) {
      flash('Action failed');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  // Transcoding happens in the background, so poll while anything is working.
  useEffect(() => {
    if (!videos.some((v) => v.status === 'processing')) return;
    const t = setInterval(loadVideos, 4000);
    return () => clearInterval(t);
  }, [videos, loadVideos]);

  /**
   * fetch() gives no upload progress, so film uploads go through XHR.
   * A coach-length clip can be hundreds of MB; a silent bar is unusable.
   */
  const xhrUpload = (url, formData, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      const token = localStorage.getItem('token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve(JSON.parse(xhr.responseText || '{}'))
        : reject(new Error(xhr.responseText || `HTTP ${xhr.status}`)));
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });

  const uploadVideo = async () => {
    if (!uploadFile) return flash('Choose a file first');
    if (!newVideo.title.trim()) return flash('Give the clip a title');
    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('title', newVideo.title);
      if (newVideo.description) fd.append('description', newVideo.description);
      fd.append('is_featured', String(newVideo.is_featured));
      await xhrUpload(`${API}/api/share/${pid}/videos/upload`, fd, setUploadPct);
      setNewVideo({ title: '', url: '', provider: 'youtube', description: '', is_featured: false });
      setUploadFile(null);
      await loadVideos();
      flash('Uploaded — converting now');
    } catch (e) {
      flash('Upload failed');
      console.error(e);
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const replaceVideo = async (videoId, file) => {
    setReplacingId(videoId);
    setReplacePct(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await xhrUpload(`${API}/api/share/${pid}/videos/${videoId}/replace`, fd, setReplacePct);
      await loadVideos();
      flash('Replaced — converting now');
    } catch (e) {
      flash('Replace failed');
      console.error(e);
    } finally {
      setReplacingId(null);
      setReplacePct(0);
    }
  };

  const setTestimonialStatus = async (id, status) => {
    await authFetch(`${API}/api/share/${pid}/testimonials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    loadTestimonials();
    flash(status === 'approved' ? 'Published ✓' : 'Hidden from the page ✓');
  };

  const deleteTestimonial = async (t) => {
    if (!window.confirm(`Delete the reference from ${t.author_name}?`)) return;
    await authFetch(`${API}/api/share/${pid}/testimonials/${t.id}`, { method: 'DELETE' });
    loadTestimonials();
  };

  const addVideo = async () => {
    if (!newVideo.title.trim() || !newVideo.url.trim()) return flash('Title and URL required');
    setBusy(true);
    try {
      const r = await authFetch(`${API}/api/share/${pid}/videos`, {
        method: 'POST',
        body: JSON.stringify({ ...newVideo, display_order: videos.length }),
      });
      if (!r.ok) throw new Error(await r.text());
      setNewVideo({ title: '', url: '', provider: 'youtube', description: '', is_featured: false });
      await loadVideos();
      flash('Clip added ✓');
    } catch (e) {
      flash('Could not add clip');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const featureVideo = async (v) => {
    await authFetch(`${API}/api/share/${pid}/videos/${v.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_featured: !v.is_featured }),
    });
    loadVideos();
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Remove "${v.title}"?`)) return;
    await authFetch(`${API}/api/share/${pid}/videos/${v.id}`, { method: 'DELETE' });
    loadVideos();
  };

  if (!pid) return null;

  const isLive = share?.is_public;

  return (
    <div className="space-y-4">

      {status && (
        <div className="text-sm text-[#00E676] font-semibold">{status}</div>
      )}

      {/* ── Publish state ─────────────────────────────────────── */}
      <Card
        icon={Globe}
        title="Public Recruiting Page"
        subtitle="A shareable page for college coaches. No login required to view it."
      >
        <div className="flex items-center gap-2.5 mb-4">
          <span className={`w-2.5 h-2.5 rounded-full ${isLive ? 'bg-[#00E676] animate-pulse' : 'bg-[#546E7A]'}`} />
          <span className={`font-bold text-sm ${isLive ? 'text-[#00E676]' : 'text-[#546E7A]'}`}>
            {isLive ? 'LIVE — anyone with the link can view' : 'Not published'}
          </span>
        </div>

        {publicUrl && (
          <div className="mb-4">
            <div className="flex items-center gap-2 bg-[#0A1628] border border-[#546E7A]/30 rounded-lg px-3 py-2.5 mb-2">
              <Link2 size={15} className="text-[#546E7A] flex-shrink-0" />
              <span className="text-[#B0BEC5] text-xs truncate flex-1 font-mono">{publicUrl}</span>
            </div>
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
              <button onClick={copyLink}
                className="flex items-center justify-center gap-2 bg-[#243B55] text-[#00D4FF] border border-[#00D4FF]/30 rounded-lg px-4 py-2.5 text-sm font-semibold">
                {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy link</>}
              </button>
              {isLive && (
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#243B55] text-[#B0BEC5] border border-[#546E7A]/30 rounded-lg px-4 py-2.5 text-sm font-semibold">
                  <ExternalLink size={15} /> Preview
                </a>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
          {!isLive ? (
            <button disabled={busy} onClick={() => doAction('publish', 'Published ✓')}
              className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50">
              <Globe size={16} /> Publish page
            </button>
          ) : (
            <button disabled={busy} onClick={() => doAction('unpublish', 'Taken offline ✓')}
              className="flex items-center justify-center gap-2 bg-[#EF5350]/10 text-[#EF5350] border border-[#EF5350]/40 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
              <EyeOff size={16} /> Take offline
            </button>
          )}
          {share?.token && (
            <button disabled={busy}
              onClick={() => {
                if (window.confirm('Generate a new link? Every link you have already shared will stop working.')) {
                  doAction('rotate-token', 'New link generated ✓');
                }
              }}
              className="flex items-center justify-center gap-2 bg-transparent text-[#546E7A] border border-[#546E7A]/40 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
              <RefreshCw size={15} /> New link
            </button>
          )}
        </div>

        {/* Search visibility. Separate from publishing: a link you hand out
            directly is a different decision from being findable on Google. */}
        {isLive && (
          <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[#00D4FF] flex-shrink-0"
              checked={share?.allow_indexing ?? true}
              onChange={async (e) => {
                await authFetch(`${API}/api/share/${pid}/indexing?allow=${e.target.checked}`, { method: 'POST' });
                await loadShare();
                flash(e.target.checked ? 'Search engines allowed ✓' : 'Hidden from search ✓');
              }} />
            <span className="min-w-0">
              <span className="block text-[#B0BEC5] text-sm">Allow Google and other search engines to index this page</span>
              <span className="block text-[#546E7A] text-xs mt-0.5">
                Turn off to keep the link shareable but unlisted. Removing a page already
                in Google's index takes time, so decide before sharing widely.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-start gap-2 mt-4 rounded-lg border border-[#FFB300]/30 bg-[#FFB300]/[0.07] px-3 py-2.5">
          <AlertTriangle size={14} className="text-[#FFB300] flex-shrink-0 mt-0.5" />
          <p className="text-[#B0BEC5] text-xs leading-relaxed">
            The public page never shows date of birth, internal grades, evaluator notes,
            or fit scores. Listed contacts should be the coach and a parent — not the player.
          </p>
        </div>
      </Card>

      {/* ── Photo ─────────────────────────────────────────────── */}
      <Card icon={ImageIcon} title="Profile Photo"
        subtitle="The first thing a coach sees. Best: standing in uniform, helmet off, shot vertically.">
        <div className="flex items-center gap-4">
          <div className="w-20 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-[#12233D]
                          border border-[#546E7A]/40 flex items-center justify-center">
            {player.photo_url
              ? <img src={player.photo_url.startsWith('http') ? player.photo_url : `${API}${player.photo_url}`}
                  alt="" className="w-full h-full object-cover" />
              : <ImageIcon size={22} className="text-[#2A4060]" />}
          </div>
          <div className="min-w-0 flex-1">
            <label className="flex items-center justify-center gap-2 w-full rounded-lg border border-dashed
                              border-[#546E7A]/50 bg-[#243B55] px-3 py-3 cursor-pointer text-sm font-semibold text-[#00D4FF]">
              <Upload size={15} />
              {photoBusy ? 'Uploading…' : player.photo_url ? 'Replace photo' : 'Upload photo'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                disabled={photoBusy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadPhoto(f);
                  e.target.value = '';
                }} />
            </label>
            <div className="text-[#546E7A] text-xs mt-1.5">JPG, PNG or WebP, up to 5MB.</div>
          </div>
        </div>
      </Card>

      {/* ── Page content ──────────────────────────────────────── */}
      <Card icon={FileText} title="Page Content" subtitle="What a coach reads first.">
        <div className="space-y-3.5">
          <Field label="Headline" hint="One line at the top of the page. Lead with what stands out.">
            <input className={input} value={form.public_headline || ''} maxLength={300}
              placeholder="One line on what stands out."
              onChange={(e) => set('public_headline', e.target.value)} />
          </Field>
          <Field label="About">
            <textarea className={input} rows={5} value={form.public_bio || ''}
              placeholder="Playing style, role in the offense, awards, camps attended, what they're working on."
              onChange={(e) => set('public_bio', e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Projected positions" hint="Widens the coaches who consider him.">
              <input className={input} value={form.projected_positions || ''} placeholder="e.g. two positions, separated by a slash"
                onChange={(e) => set('projected_positions', e.target.value)} />
            </Field>
            <Field label="Jersey number">
              <input className={input} type="number" inputMode="numeric" value={form.jersey_number ?? ''}
                onChange={(e) => set('jersey_number', e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Hudl profile URL">
              <input className={input} type="url" value={form.hudl_url || ''}
                placeholder="https://www.hudl.com/profile/..."
                onChange={(e) => set('hudl_url', e.target.value)} />
            </Field>
            <Field label="X / Twitter handle" hint="How most college coaches make first contact.">
              <input className={input} value={form.twitter_handle || ''} placeholder="@handle"
                onChange={(e) => set('twitter_handle', e.target.value)} />
            </Field>
          </div>
          <Field label="NCAA Eligibility Center ID" hint="Optional, but coaches look for it.">
            <input className={input} value={form.ncaa_id || ''}
              onChange={(e) => set('ncaa_id', e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <div className="sm:col-span-2">
              <Field label="High school" hint="Shown at the top of the public page.">
                <input className={input} value={form.high_school || ''}
                  onChange={(e) => set('high_school', e.target.value)} />
              </Field>
            </div>
            <Field label="State">
              <input className={input} maxLength={2} value={form.hs_state || ''}
                placeholder="AL"
                onChange={(e) => set('hs_state', e.target.value.toUpperCase())} />
            </Field>
          </div>
          <Field label="Hometown">
            <input className={input} value={form.hometown || ''}
              onChange={(e) => set('hometown', e.target.value)} />
          </Field>
        </div>
      </Card>

      {/* ── Recruiting measurables ────────────────────────────── */}
      <Card icon={Ruler} title="Recruiting Measurables"
        subtitle={player?.position
          ? `What college coaches screen on for ${player.position}.`
          : 'Set a position on the Player Profile tab to see the right measurables.'}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-3.5">
          {measurables.map((m) => (
            <Field key={m.field_key}
              label={m.unit ? `${m.display_name} (${m.unit})` : m.display_name}
              hint={m.help_text || null}>
              <input className={input}
                type={m.input_type === 'text' ? 'text' : 'number'}
                inputMode={m.input_type === 'text' ? undefined : 'decimal'}
                step={m.step || undefined}
                placeholder={m.placeholder || ''}
                value={form[m.field_key] ?? ''}
                onChange={(e) => set(m.field_key, e.target.value)} />
            </Field>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label="How were these measured?"
            hint="Coaches discount self-reported numbers. Labeling them honestly protects the rest of the page.">
            <select className={input} value={form.measurables_source || 'self_reported'}
              onChange={(e) => set('measurables_source', e.target.value)}>
              {MEASURABLE_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Event / source name" hint="e.g. “Rivals Camp Birmingham”. Shown only if filled in.">
            <input className={input} value={form.measurables_event || ''}
              onChange={(e) => set('measurables_event', e.target.value)} />
          </Field>
        </div>
      </Card>

      {/* ── Contact ───────────────────────────────────────────── */}
      <Card icon={Mail} title="Contact"
        subtitle="Coach and guardian only — this is how college programs contact an underclassman.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label="Coach name">
            <input className={input} value={form.coach_name || ''}
              onChange={(e) => set('coach_name', e.target.value)} />
          </Field>
          <Field label="Coach title">
            <input className={input} value={form.coach_title || ''} placeholder="e.g. Head Coach"
              onChange={(e) => set('coach_title', e.target.value)} />
          </Field>
          <Field label="Coach email">
            <input className={input} type="email" value={form.coach_email || ''}
              onChange={(e) => set('coach_email', e.target.value)} />
          </Field>
          <Field label="Coach phone">
            <input className={input} type="tel" value={form.coach_phone || ''}
              onChange={(e) => set('coach_phone', e.target.value)} />
          </Field>
          <Field label="Phone note" hint="e.g. “School office — ask for Coach Gay”. Shown under the number.">
            <input className={input} value={form.coach_phone_note || ''}
              onChange={(e) => set('coach_phone_note', e.target.value)} />
          </Field>
          <Field label="Coach X / Twitter">
            <input className={input} value={form.coach_twitter || ''} placeholder="@handle"
              onChange={(e) => set('coach_twitter', e.target.value)} />
          </Field>
          <Field label="Parent / guardian name">
            <input className={input} value={form.guardian_name || ''}
              onChange={(e) => set('guardian_name', e.target.value)} />
          </Field>
          <Field label="Guardian email">
            <input className={input} type="email" value={form.guardian_email || ''}
              onChange={(e) => set('guardian_email', e.target.value)} />
          </Field>
          <Field label="Guardian phone">
            <input className={input} type="tel" value={form.guardian_phone || ''}
              onChange={(e) => set('guardian_phone', e.target.value)} />
          </Field>
        </div>
      </Card>

      <button onClick={handleSave} disabled={saving}
        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
        <Save size={16} /> {saving ? 'Saving…' : 'Save page details'}
      </button>

      {/* ── References ────────────────────────────────────────── */}
      <Card icon={Quote} title="References"
        subtitle="Nothing appears on the public page until you approve it.">

        {testimonials.length === 0 ? (
          <p className="text-[#546E7A] text-sm">
            No references yet. Anyone with the link can submit one from the public page.
          </p>
        ) : (
          <div className="space-y-2.5">
            {testimonials.map((t) => (
              <div key={t.id}
                className={`rounded-lg border px-3.5 py-3 ${
                  t.status === 'pending'
                    ? 'border-[#FFB300]/40 bg-[#FFB300]/[0.06]'
                    : t.status === 'approved'
                      ? 'border-[#00E676]/30 bg-[#00E676]/[0.05]'
                      : 'border-[#546E7A]/25 bg-[#0A1628] opacity-60'
                }`}>

                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-white font-semibold text-sm">{t.author_name}</div>
                    <div className="text-[#546E7A] text-xs">
                      {[t.author_title, t.author_organization].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 flex-shrink-0 flex items-center gap-1 ${
                    t.status === 'pending'
                      ? 'text-[#FFB300] bg-[#FFB300]/12 border border-[#FFB300]/30'
                      : t.status === 'approved'
                        ? 'text-[#00E676] bg-[#00E676]/12 border border-[#00E676]/30'
                        : 'text-[#546E7A] border border-[#546E7A]/30'
                  }`}>
                    {t.status === 'pending' && <Clock size={10} />}
                    {t.status.toUpperCase()}
                  </span>
                </div>

                <p className="text-[#C8D8E8] text-sm leading-relaxed whitespace-pre-line">{t.body}</p>

                {(t.author_email || t.author_phone) && (
                  <div className="text-[#546E7A] text-xs mt-2">
                    Verify: {[t.author_email, t.author_phone].filter(Boolean).join(' · ')}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#546E7A]/20">
                  {t.status !== 'approved' && (
                    <button onClick={() => setTestimonialStatus(t.id, 'approved')}
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#00E676]
                                 border border-[#00E676]/40 rounded-lg px-3 py-2">
                      <ThumbsUp size={13} /> Publish
                    </button>
                  )}
                  {t.status !== 'rejected' && (
                    <button onClick={() => setTestimonialStatus(t.id, 'rejected')}
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#FFB300]
                                 border border-[#FFB300]/40 rounded-lg px-3 py-2">
                      <ThumbsDown size={13} /> {t.status === 'approved' ? 'Unpublish' : 'Reject'}
                    </button>
                  )}
                  <button onClick={() => deleteTestimonial(t)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#546E7A]
                               border border-[#546E7A]/30 rounded-lg px-3 py-2">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Film clips ────────────────────────────────────────── */}
      <Card icon={Film} title="Film Clips"
        subtitle="Upload film to host it here, or paste a YouTube / Hudl / Vimeo link.">

        {videos.length > 0 && (
          <div className="space-y-2 mb-4">
            {videos.map((v) => (
              <div key={v.id} className="bg-[#0A1628] border border-[#546E7A]/25 rounded-lg px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {v.is_featured && <Star size={12} className="text-[#FFB300] flex-shrink-0" fill="#FFB300" />}
                      <span className="text-white text-sm font-medium truncate">{v.title}</span>
                      {v.status === 'processing' && (
                        <span className="text-[10px] font-bold text-[#FFB300] bg-[#FFB300]/12 border border-[#FFB300]/30 rounded px-1.5 py-0.5">
                          PROCESSING
                        </span>
                      )}
                      {v.status === 'failed' && (
                        <span className="text-[10px] font-bold text-[#EF5350] bg-[#EF5350]/12 border border-[#EF5350]/30 rounded px-1.5 py-0.5">
                          FAILED
                        </span>
                      )}
                      {v.status === 'hidden' && (
                        <span className="text-[10px] font-bold text-[#B0BEC5] bg-[#546E7A]/15 border border-[#546E7A]/40 rounded px-1.5 py-0.5">
                          HIDDEN
                        </span>
                      )}
                      {v.source_type === 'upload' && v.status === 'ready' && (
                        <span className="text-[10px] font-bold text-[#00E676] bg-[#00E676]/12 border border-[#00E676]/30 rounded px-1.5 py-0.5">
                          HOSTED
                        </span>
                      )}
                    </div>
                    <div className="text-[#546E7A] text-xs truncate mt-0.5">
                      {v.source_type === 'upload'
                        ? [
                            v.duration_seconds ? `${Math.round(v.duration_seconds)}s` : null,
                            v.file_size ? `${(v.file_size / 1024 / 1024).toFixed(1)} MB` : null,
                            v.width ? `${v.width}×${v.height}` : null,
                          ].filter(Boolean).join(' · ') || 'Uploaded film'
                        : v.url}
                    </div>
                    {v.status === 'failed' && v.status_detail && (
                      <div className="text-[#EF5350] text-[11px] mt-1 break-words">{v.status_detail}</div>
                    )}
                  </div>

                  <button onClick={() => featureVideo(v)} title={v.is_featured ? 'Unfeature' : 'Make featured reel'}
                    className={`p-2 rounded ${v.is_featured ? 'text-[#FFB300]' : 'text-[#546E7A]'}`}>
                    <Star size={15} />
                  </button>
                  <button onClick={() => deleteVideo(v)} title="Remove clip" className="p-2 rounded text-[#546E7A]">
                    <Trash2 size={15} />
                  </button>
                </div>

                {/* Swap in newer film without losing the title, caption or position */}
                {v.source_type === 'upload' && (
                  <label className="mt-2 flex items-center justify-center gap-2 text-xs font-semibold
                                    text-[#00D4FF] border border-[#00D4FF]/30 rounded-lg py-2 cursor-pointer">
                    <RefreshCw size={13} />
                    {replacingId === v.id ? `Replacing… ${replacePct}%` : 'Replace with newer film'}
                    <input type="file" accept="video/*" className="hidden"
                      disabled={replacingId !== null}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) replaceVideo(v.id, f);
                        e.target.value = '';
                      }} />
                  </label>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Mode switch */}
        <div className="flex gap-2 mb-4 border-t border-[#546E7A]/20 pt-4">
          {[['upload', 'Upload film'], ['link', 'Paste a link']].map(([mode, label]) => (
            <button key={mode} onClick={() => setAddMode(mode)}
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${
                addMode === mode
                  ? 'bg-[#00D4FF]/15 border-[#00D4FF]/50 text-[#00D4FF]'
                  : 'bg-transparent border-[#546E7A]/30 text-[#546E7A]'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {addMode === 'upload' ? (
          <div className="space-y-3.5">
            <Field label="Clip title" hint="Be specific — “Pass pro vs. #55, Week 3” beats “Highlights”.">
              <input className={input} value={newVideo.title}
                onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} />
            </Field>
            <Field label="Caption (optional)">
              <input className={input} value={newVideo.description}
                onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })} />
            </Field>
            <Field label="Video file"
              hint="MP4, MOV, M4V, WebM or MKV, up to 500MB. Converted automatically so it plays on every browser.">
              <label className={`flex items-center gap-3 w-full rounded-lg border border-dashed px-3 py-4 cursor-pointer transition-colors ${
                uploadFile ? 'border-[#00E676]/50 bg-[#00E676]/[0.06]' : 'border-[#546E7A]/50 bg-[#243B55]'
              }`}>
                {uploadFile
                  ? <CheckIcon size={20} className="text-[#00E676] flex-shrink-0" />
                  : <Upload size={20} className="text-[#00D4FF] flex-shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="block text-white text-sm font-semibold truncate">
                    {uploadFile ? uploadFile.name : 'Choose a video file'}
                  </span>
                  <span className="block text-[#546E7A] text-xs mt-0.5">
                    {uploadFile
                      ? `${(uploadFile.size / 1024 / 1024).toFixed(1)} MB — tap to choose a different file`
                      : 'Tap to browse your device'}
                  </span>
                </span>
                <input type="file" accept="video/*" className="hidden"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
              </label>
            </Field>
            <label className="flex items-center gap-2.5 text-[#B0BEC5] text-sm cursor-pointer">
              <input type="checkbox" checked={newVideo.is_featured} className="w-4 h-4 accent-[#00D4FF]"
                onChange={(e) => setNewVideo({ ...newVideo, is_featured: e.target.checked })} />
              Make this the featured reel at the top of the page
            </label>

            {uploading && (
              <div>
                <div className="h-2 bg-[#0A1628] rounded-full overflow-hidden">
                  <div className="h-full bg-[#00D4FF] rounded-full transition-all"
                    style={{ width: `${uploadPct}%` }} />
                </div>
                <div className="text-[#546E7A] text-xs mt-1.5">
                  {uploadPct < 100 ? `Uploading… ${uploadPct}%` : 'Converting film — this can take a minute.'}
                </div>
              </div>
            )}

            <button onClick={uploadVideo} disabled={uploading || !uploadFile}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
              <Upload size={16} /> {uploading ? 'Uploading…' : 'Upload film'}
            </button>
          </div>
        ) : (
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <div className="sm:col-span-2">
                <Field label="Clip title">
                  <input className={input} value={newVideo.title}
                    onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })} />
                </Field>
              </div>
              <Field label="Source">
                <select className={input} value={newVideo.provider}
                  onChange={(e) => setNewVideo({ ...newVideo, provider: e.target.value })}>
                  {PROVIDERS.map((pr) => <option key={pr.value} value={pr.value}>{pr.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="URL" hint="YouTube and Vimeo play inline. Hudl blocks embedding, so it links out.">
              <input className={input} type="url" value={newVideo.url} placeholder="https://youtube.com/watch?v=..."
                onChange={(e) => setNewVideo({ ...newVideo, url: e.target.value })} />
            </Field>
            <Field label="Caption (optional)">
              <input className={input} value={newVideo.description}
                onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2.5 text-[#B0BEC5] text-sm cursor-pointer">
              <input type="checkbox" checked={newVideo.is_featured} className="w-4 h-4 accent-[#00D4FF]"
                onChange={(e) => setNewVideo({ ...newVideo, is_featured: e.target.checked })} />
              Make this the featured reel at the top of the page
            </label>
            <button onClick={addVideo} disabled={busy}
              className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50">
              <Plus size={16} /> Add clip
            </button>
          </div>
        )}
      </Card>

    </div>
  );
};

export default RecruitingTab;
